<template>
  <div
    class="cb-registration"
    v-documentClick="{
      handler: 'handleDocumentClick',
    }"
  >
    <div class="cb-registration__img-container">
      <img
        alt="ADT Cellbridge Device"
        src="../assets/images/complete-registration.jpg"
        v-if="screenSize === 'desktop'"
      />
    </div>
    <div class="cb-registration__container">
      <button
        id="open-nav"
        aria-label="Open Navigation"
        @click="handleNavButtonClick"
        class="nav-button"
      >
        <img alt src="../assets/icons/menu-icon-blue.svg" />
      </button>
      <cb-navigation
        v-if="isNavigationOpen"
        class="cb-navigation--desktop"
      ></cb-navigation>

      <div
        class="cb-registration__container__content"
        v-show="step === 'complete'"
      >
        <h1 class="cb-heading-4" tabindex="-1" ref="title">
          Complete your MyADT Registration
        </h1>
        <p :class="{ error: formErrorMessage && formError }" class="cb-body-1">
          Easily manage your ADT account, make payments, set up EasyPay<sup
            >®</sup
          >
          automatic payments and more.
        </p>
        <p
          class="cb-registration__error cb-body-2"
          v-show="formErrorMessage && formError"
          aria-live="polite"
        >
          {{ formErrorMessage }}
        </p>
        <form autocomplete="off">
          <div class="cb-registration__container__content__name">
            <cb-text-input
              :error="$v.formData.firstName.$error"
              :errorText="formErrors.firstName"
              @blur="handleBlur('firstName', 'First name must be entered.')"
              @focus="$v.formData.firstName.$reset()"
              @input="
                setFieldErrorMessage('firstName', 'First name must be entered.')
              "
              class="cb-registration__container__content__input space-right"
              id="firstName"
              label="First Name"
              required
              type="text"
              v-model="formData.firstName"
            ></cb-text-input>
            <cb-text-input
              :error="$v.formData.lastName.$error"
              :errorText="formErrors.lastName"
              @blur="handleBlur('lastName', 'Last name must be entered.')"
              @focus="$v.formData.lastName.$reset()"
              @input="
                setFieldErrorMessage('lastName', 'Last name must be entered.')
              "
              class="cb-registration__container__content__input space-left"
              id="lastName"
              label="Last Name"
              required
              type="text"
              v-model="formData.lastName"
            ></cb-text-input>
          </div>
          <cb-text-input
            :error="$v.formData.emailAddress.$error"
            :errorText="formErrors.emailAddress"
            @blur="
              handleBlur('emailAddress', 'Please enter a valid email address.')
            "
            @focus="handleFocus('emailAddress')"
            @input="
              setFieldErrorMessage(
                'emailAddress',
                'Please enter a valid email address.',
              )
            "
            class="cb-registration__container__content__input cb-registration__email"
            id="email"
            label="Email Address"
            required
            type="text"
            v-model="formData.emailAddress"
          ></cb-text-input>
          <div class="cb-registration__container__content__password-container">
            <div
              class="cb-registration__container__content__password space-right"
              ref="password-input"
            >
              <cb-text-input
                :error="$v.formData.password.$error"
                :errorText="formErrors.password"
                @blur="handlePasswordBlur"
                @focus="handlePasswordFocus"
                @input="
                  setFieldErrorMessage(
                    'password',
                    'Please enter a valid password.',
                  )
                "
                class="cb-registration__container__content__input"
                id="password"
                label="Password"
                required
                type="password"
                v-model="formData.password"
              ></cb-text-input>
              <button
                @click.prevent="handleInfoIconClick()"
                type="button"
                aria-label="Password Requirements Tooltip"
                :aria-expanded="isPasswordTooltipOpen ? 'true' : 'false'"
                ref="password-tooltip-btn"
              >
                <img alt src="../assets/icons/info.svg" />
              </button>
              <div
                class="cb-tooltip"
                ref="password-tooltip"
                v-if="isPasswordTooltipOpen"
              >
                <div class="cb-tooltip__arrow"></div>
                <p class="cb-overline cb-tooltip__header">
                  Password requirements:
                </p>
                <p
                  :class="{ satisfied: passwordMinLength }"
                  class="cb-overline cb-tooltip__requirement"
                >
                  8 character minimum
                  <span class="sr-only">
                    {{
                      passwordMinLength
                        ? ', requirement met'
                        : ', requirement not met'
                    }}
                  </span>
                </p>
                <p
                  :class="{ satisfied: passwordHasNumber }"
                  class="cb-overline cb-tooltip__requirement"
                >
                  At least 1 number
                  <span class="sr-only">
                    {{
                      passwordHasNumber
                        ? ', requirement met'
                        : ', requirement not met'
                    }}
                  </span>
                </p>
                <p
                  :class="{ satisfied: passwordHasLetterOrSymbol }"
                  class="cb-overline cb-tooltip__requirement"
                >
                  At least 1 letter or symbol (! @ # etc.)
                  <span class="sr-only">
                    {{
                      passwordHasLetterOrSymbol
                        ? ', requirement met'
                        : ', requirement not met'
                    }}
                  </span>
                </p>
                <p
                  :class="{ satisfied: passwordIsUnique }"
                  class="cb-overline cb-tooltip__requirement"
                >
                  Cannot use your email address, username or actual name
                  <span class="sr-only">
                    {{
                      passwordIsUnique
                        ? ', requirement met'
                        : ', requirement not met'
                    }}
                  </span>
                </p>
                <p
                  :class="{ satisfied: passwordNoDuplicateConsecutive }"
                  class="cb-overline cb-tooltip__requirement"
                >
                  No more than 2 consecutive identical characters
                  <span class="sr-only">
                    {{
                      passwordNoDuplicateConsecutive
                        ? ', requirement met'
                        : ', requirement not met'
                    }}
                  </span>
                </p>
              </div>
            </div>
            <cb-text-input
              :error="$v.formData.confirmPassword.$error"
              :errorText="formErrors.confirmPassword"
              @blur="handleConfirmPasswordBlur"
              @focus="handleFocus('confirmPassword')"
              @input="
                setFieldErrorMessage(
                  'confirmPassword',
                  'Passwords entered do not match.',
                )
              "
              class="cb-registration__container__content__input space-left"
              id="confirmPassword"
              label="Confirm Password"
              required
              type="password"
              v-model="formData.confirmPassword"
            ></cb-text-input>
          </div>
          <cb-select
            :options="securityQuestions"
            :error="$v.formData.question1.$error"
            :errorText="formErrors.question1"
            @blur="
              handleBlur('question1', 'Please select a security question.')
            "
            @focus="$v.formData.question1.$reset()"
            @input="
              setFieldErrorMessage(
                'question1',
                'Please select a security question.',
              )
            "
            id="securityQuestions"
            label="Security Questions"
            v-model="formData.question1"
          ></cb-select>
          <cb-text-input
            :error="$v.formData.secretAnswer1.$error"
            :errorText="formErrors.secretAnswer1"
            @blur="
              handleBlur(
                'secretAnswer1',
                'Security question answer must be entered',
              )
            "
            @focus="$v.formData.secretAnswer1.$reset()"
            @input="
              setFieldErrorMessage(
                'secretAnswer1',
                'Security question answer must be entered',
              )
            "
            class="cb-registration__container__content__input cb-registration__answer"
            id="securityQuestionAnswer"
            label="Answer"
            required
            type="text"
            v-model="formData.secretAnswer1"
          ></cb-text-input>

          <div class="cb-registration__container__content__footer">
            <div class="divider"></div>
            <div>
              <button
                @click.prevent="handleContinueClick"
                class="cb-button cb-button--blue continue"
                type="submit"
              >
                Continue
              </button>
            </div>
          </div>
        </form>
      </div>
      <div
        class="cb-registration__container__content cb-registration__container__content--confirm"
        v-show="step === 'confirm'"
      >
        <h1 class="cb-heading-4" tabindex="-1" ref="confirmationTitle">
          Confirm account information
        </h1>
        <p
          :class="{
            error: globalErrorMessage || (formErrorMessage && formError),
          }"
          class="cb-body-1"
        >
          Please comfirm that your account information is correct.
        </p>
        <p
          class="cb-registration__error cb-body-2"
          v-show="globalErrorMessage"
          v-html="globalErrorMessage"
          aria-live="polite"
        ></p>
        <p
          class="cb-registration__error cb-body-2"
          v-show="formErrorMessage && formError"
          aria-live="polite"
        >
          {{ formErrorMessage }}
        </p>
        <form autocomplete="off">
          <h2 class="cb-heading-6">Account</h2>
          <div class="divider"></div>
          <cb-text-input
            :error="$v.formData.firstName.$error"
            :errorText="formErrors.firstName"
            @blur="handleBlur('firstName', 'First name must be entered.')"
            @focus="$v.formData.firstName.$reset()"
            @input="
              setFieldErrorMessage('firstName', 'First name must be entered.')
            "
            :isDisabled="true"
            class="cb-registration__container__content__input space-right"
            id="confirmFirstName"
            label="First Name"
            required
            type="text"
            v-model="formData.firstName"
          ></cb-text-input>
          <cb-text-input
            :error="$v.formData.lastName.$error"
            :errorText="formErrors.lastName"
            @blur="handleBlur('lastName', 'Last name must be entered.')"
            @focus="$v.formData.lastName.$reset()"
            @input="
              setFieldErrorMessage('lastName', 'Last name must be entered.')
            "
            :isDisabled="true"
            class="cb-registration__container__content__input space-left"
            id="confirmLastName"
            label="Last Name"
            required
            type="text"
            v-model="formData.lastName"
          ></cb-text-input>
          <cb-text-input
            :error="$v.formData.emailAddress.$error"
            :errorText="formErrors.emailAddress"
            @blur="
              handleBlur('emailAddress', 'Please enter a valid email address.')
            "
            @focus="handleFocus('emailAddress')"
            @input="
              setFieldErrorMessage(
                'emailAddress',
                'Please enter a valid email address.',
              )
            "
            :isDisabled="true"
            class="cb-registration__container__content__input"
            id="confirmEmail"
            label="Email Address"
            required
            type="text"
            v-model="formData.emailAddress"
          ></cb-text-input>
          <h2 class="cb-heading-6">Security Question</h2>
          <div class="divider"></div>
          <cb-text-input
            :error="$v.formData.secretAnswer1.$error"
            :errorText="formErrors.secretAnswer1"
            @blur="
              handleBlur(
                'secretAnswer1',
                'Security question answer must be entered.',
              )
            "
            @focus="$v.formData.secretAnswer1.$reset()"
            @input="
              setFieldErrorMessage(
                'secretAnswer1',
                'Security question answer must be entered.',
              )
            "
            :isDisabled="true"
            :label="formData.question1 || 'Security Question'"
            class="cb-registration__container__content__input"
            id="confirmSecurityQuestionAnswer"
            required
            type="text"
            v-model="formData.secretAnswer1"
          ></cb-text-input>
          <div class="cb-registration__container__content__footer">
            <div class="divider"></div>
            <div>
              <label class="cb-checkbox">
                <input
                  @blur="siteUsageCheckedFocus = false"
                  @focus="siteUsageCheckedFocus = true"
                  checked="checked"
                  required
                  type="checkbox"
                  v-model="formData.siteUsageChecked"
                />
                <span
                  :class="
                    siteUsageCheckedFocus
                      ? 'cb-checkbox__checkmark--outline'
                      : ''
                  "
                  class="cb-checkbox__checkmark"
                ></span>
                <span class="cb-checkbox__label cb-caption">
                  I agree with the MyADT
                  <a
                    @click="trackLinks"
                    class="cb-link"
                    href="https://www.myadt.com/usage-agreement"
                    rel="noopener noreferrer"
                    target="_blank"
                    >usage agreement</a
                  >
                </span>
              </label>
              <span
                v-if="formData.siteUsageChecked === false"
                class="sr-only"
                >{{ formErrors.siteUsageChecked }}</span
              >
              <cb-button
                type="submit"
                :loading="loading"
                @click="handleRegisterClick"
                :paddingLeft="true"
                >Register Account</cb-button
              >
            </div>
          </div>
        </form>
      </div>
    </div>
    <cb-navigation
      v-if="isNavigationOpen"
      class="cb-navigation--mobile"
    ></cb-navigation>
  </div>
</template>

<script>
/* global gtag */
import {
  required,
  sameAs,
  email,
  minLength,
  helpers,
  not,
} from 'vuelidate/lib/validators';
import { mapState, mapActions, mapMutations } from 'vuex';
import axios from '../utility/axios';
import TextInput from '../components/shared/TextInput.vue';
import Select from '../components/shared/Select.vue';
import Button from '../components/shared/Button.vue';
import Navigation from '../components/NavigationModal.vue';

const hasNumber = helpers.regex('alpha', /\d/);
const hasLetterOrSymbol = helpers.regex(
  'alpha',
  /[a-zA-Z!@#$%^&*(),.?":{}|<>]/,
);
const hasDuplicateConsecutive = helpers.regex('alpha', /(.)\1\1+/);

export default {
  data() {
    return {
      step: 'complete',
      formData: {
        firstName: '',
        lastName: '',
        emailAddress: '',
        password: '',
        confirmPassword: '',
        question1: '',
        secretAnswer1: '',
        siteUsageChecked: true,
      },
      formErrors: {
        firstName: 'First name must be entered.',
        lastName: 'Last name must be entered.',
        emailAddress: 'Please enter a valid email address.',
        password: 'Please enter a valid password.',
        confirmPassword: 'Passwords entered do not match.',
        question1: 'Please select a security question.',
        secretAnswer1: 'Security question answer must be entered.',
        siteUsageChecked: 'Usage agreement must be checked.',
      },
      formError: false,
      formErrorMessage: '',
      loading: false,
      siteUsageCheckedFocus: false,
      isPasswordTooltipOpen: false,
      hasConfirmPasswordBeenEdited: false,
      securityQuestions: [],
    };
  },
  validations: {
    formData: {
      firstName: {
        required,
      },
      lastName: {
        required,
      },
      emailAddress: {
        required,
        email,
      },
      password: {
        required,
        hasNumber,
        hasLetterOrSymbol,
        minLength: minLength(8),
        notSameAsEmail: not(sameAs('emailAddress')),
        notSameAsFirstName: not(sameAs('firstName')),
        notSameAsLastName: not(sameAs('lastName')),
        notDuplicateConsecutive: not(hasDuplicateConsecutive),
      },
      confirmPassword: {
        required,
        sameAs: sameAs('password'),
      },
      question1: {
        required,
      },
      secretAnswer1: {
        required,
      },
      siteUsageChecked: {
        sameAs: sameAs(() => true),
      },
    },
  },
  beforeMount() {
    axios
      .get('/customer/security-questions')
      .then((res) => {
        this.securityQuestions = res.data.questions;
      })
      .catch((error) => {
        gtag('event', 'APIError', {
          event_label: `api error: ${error.message}`,
          event_category: 'completeRegistration',
          value: 1,
        });
        this.$router.push('/error');
      });
  },
  mounted() {
    this.$refs.title.focus();
    document.title = 'CellBridge - Complete your MyADT Registration';
  },
  methods: {
    handleBlur(fieldName, message) {
      this.$v.formData[fieldName].$touch();
      this.setFieldErrorMessage(fieldName, message);
      this.setFormErrorMessage();
    },
    handleFocus(field) {
      this.isPasswordTooltipOpen = false;
      this.$v.formData[field].$reset();
    },
    setFieldErrorMessage(fieldName, message) {
      if (this.$v.formData[fieldName].$invalid) {
        this.formErrors[fieldName] = message;
      } else {
        this.formErrors[fieldName] = '';
      }
    },
    handleRegisterClick() {
      this.$v.formData.$touch();
      if (!this.$v.formData.$anyError) {
        this.loading = true;
        this.registerUser(this.formData);
        gtag('event', 'registerClick', {
          event_label: 'register account',
          event_category: 'completeRegistration',
          value: 1,
        });
      } else {
        this.setFormErrorMessage();
        this.formError = true;
      }
    },
    handleContinueClick() {
      this.$v.formData.$touch();
      if (!this.$v.formData.$anyError) {
        this.formData.siteUsageChecked = false;
        this.formError = false;
        this.step = 'confirm';
        gtag('event', 'continueClick', {
          event_label: 'continue',
          event_category: 'completeRegistration',
          value: 1,
        });
      } else {
        this.setFormErrorMessage();
        this.formError = true;
      }
    },
    handleInfoIconClick() {
      this.isPasswordTooltipOpen = !this.isPasswordTooltipOpen;
    },
    handleDocumentClick(e) {
      // Close tooltip for clicks outside of tooltip, icon, or password field
      const passwordTooltip = this.$refs['password-tooltip'];
      const passwordTooltipBtn = this.$refs['password-tooltip-btn'];
      const passwordInput = this.$refs['password-input'];

      if (
        passwordTooltip
        && !passwordTooltip.contains(e.target)
        && !passwordTooltipBtn.contains(e.target)
        && !passwordInput.contains(e.target)
      ) {
        this.isPasswordTooltipOpen = false;
      }
    },
    handlePasswordFocus() {
      this.$v.formData.password.$reset();
      if (!this.isPasswordValid) {
        this.isPasswordTooltipOpen = true;
      }
    },
    handlePasswordBlur() {
      this.handleBlur('password', 'Please enter a valid password.');
      if (this.hasConfirmPasswordBeenEdited) {
        this.handleBlur('confirmPassword', 'Passwords entered do not match.');
      }
    },
    handleConfirmPasswordBlur() {
      this.hasConfirmPasswordBeenEdited = true;
      this.handleBlur('confirmPassword', 'Passwords entered do not match.');
    },
    handleNavButtonClick() {
      this.setIsNavigationModalOpen(true);
      gtag('event', 'openNavClick', {
        event_label: 'open navigation',
        event_category: 'completeRegistration',
        value: 1,
      });
    },
    setFormErrorMessage() {
      const resultArr = [];
      Object.keys(this.$v.formData).forEach((key) => {
        if (this.$v.formData[key].$invalid === true) {
          resultArr.push(key);
        }
      });

      if (resultArr.length === 0) {
        this.formErrorMessage = false;
        return;
      }

      if (resultArr.length === 1) {
        this.formErrorMessage = this.formErrors[resultArr[0]];
        return;
      }

      this.formErrorMessage = 'All fields are required.';
    },
    trackLinks(evt) {
      gtag('event', 'linkClick', {
        event_label: evt.target.innerHTML.toLowerCase(),
        event_category: 'completeRegistration',
        value: 1,
      });
    },
    ...mapActions(['registerUser']),
    ...mapMutations(['setIsNavigationModalOpen']),
  },
  watch: {
    step() {
      if (this.step === 'confirm') {
        this.$nextTick(() => {
          this.$refs.confirmationTitle.focus();
        });
      }
    },
    isPasswordValid() {
      if (this.isPasswordValid) {
        this.isPasswordTooltipOpen = false;
      } else {
        this.isPasswordTooltipOpen = true;
      }
    },
    globalErrorMessage() {
      if (this.globalErrorMessage) {
        this.loading = false;
      }
    },
    isModalOpen() {
      if (this.isModalOpen) {
        this.loading = false;
      }
    },
  },
  computed: {
    passwordMinLength() {
      if (
        this.$v.formData.password.minLength
        && this.formData.password.length > 0
      ) {
        return true;
      }
      return false;
    },
    passwordHasNumber() {
      if (
        this.$v.formData.password.hasNumber
        && this.formData.password.length > 0
      ) {
        return true;
      }
      return false;
    },
    passwordHasLetterOrSymbol() {
      if (
        this.$v.formData.password.hasLetterOrSymbol
        && this.formData.password.length > 0
      ) {
        return true;
      }
      return false;
    },
    passwordIsUnique() {
      if (
        this.$v.formData.password.notSameAsEmail
        && this.$v.formData.password.notSameAsFirstName
        && this.$v.formData.password.notSameAsLastName
        && this.formData.password.length > 0
      ) {
        return true;
      }
      return false;
    },
    passwordNoDuplicateConsecutive() {
      if (
        this.$v.formData.password.notDuplicateConsecutive
        && this.formData.password.length > 0
      ) {
        return true;
      }
      return false;
    },
    isPasswordValid() {
      return !this.$v.formData.password.$invalid;
    },
    ...mapState({
      screenSize: (state) => state.screenSize,
      globalErrorMessage: (state) => state.formErrorMessage,
      isModalOpen: (state) => state.modal.isOpen,
      isNavigationOpen: (state) => state.navigationModal.isOpen,
    }),
  },
  components: {
    cbTextInput: TextInput,
    cbSelect: Select,
    cbButton: Button,
    cbNavigation: Navigation,
  },
};
</script>

<style lang="scss" scoped>
.cb-registration {
  background-color: $azure;
  box-sizing: border-box;
  min-height: 100vh;
  padding: 20px 20px 40px;

  &__img-container {
    display: none;
  }

  &__error {
    color: $dark-red;
    margin-bottom: 8px;
    text-align: left;
  }

  .cb-navigation {
    &--desktop {
      display: none;
    }
  }

  &__container {
    position: relative;
    background-color: white;
    padding: 28px 20px 40px;
    border-radius: 10px;
    max-width: 562px;
    width: 100%;
    box-sizing: border-box;
    margin: 0 auto;

    .nav-button {
      position: absolute;
      border: 0;
      padding: 0;
      top: 20px;
      right: 20px;
      background-color: transparent;
      height: 24px;
      width: 24px;

      img {
        height: 100%;
        width: 100%;
      }
    }

    &__content {
      .cb-heading-4 {
        margin-bottom: 12px;
      }

      .cb-heading-6 {
        margin-bottom: 8px;
      }

      .cb-body-1 {
        margin-bottom: 29px;

        &.error {
          margin-bottom: 2px;
        }
      }

      form > .divider {
        width: 100%;
        height: 1px;
        background-color: $lighter-grey;
        margin-bottom: 16px;
      }

      &__input,
      .cb-select {
        margin-bottom: 16px;

        &.cb-registration__email,
        &.cb-registration__answer {
          flex-grow: 0;
        }
      }

      &__password {
        display: flex;
        align-items: center;
        position: relative;

        button {
          border: none;
          background-color: transparent;
          padding: 0;
          margin: 0 0 16px 8px;
          height: 25px;
          width: 25px;
        }

        .cb-tooltip {
          box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
          position: absolute;
          background-color: white;
          box-sizing: border-box;
          color: $slate-grey;
          width: 188px;
          right: 40px;
          top: -100px;
          z-index: 2;
          padding: 20px;
          border-radius: 10px;

          &__header {
            font-weight: 600;
            margin-bottom: 8px;
          }

          &__requirement {
            position: relative;
            padding-left: 20px;
            margin-bottom: 5px;

            &::before {
              position: absolute;
              content: ' ';
              height: 7px;
              width: 7px;
              left: 2px;
              top: 3px;
              border-radius: 50%;
              border: 1px solid $dark-red;
            }

            &.satisfied {
              &::before {
                height: 9px;
                width: 9px;
                border: none;
                background-color: $dark-green;
              }
            }
          }

          &::before {
            right: 0;
            top: 50%;
            border: none;
            transform: translateY(-50%);
            content: ' ';
            height: 74px;
            width: 19px;
            background-color: white;
            z-index: 2;
            position: absolute;
            pointer-events: none;
          }

          &::after {
            position: absolute;
            content: ' ';
            background-color: white;
            box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
            width: 20px;
            height: 20px;
            right: -10px;
            top: 50%;
            transform: translateY(-50%) rotate(45deg);
          }

          .assistance {
            margin: 12px 0 4px;
          }
        }
      }

      .cb-checkbox {
        margin: 28px 0 40px;
      }

      &__footer {
        margin-top: 40px;

        div:last-of-type {
          width: 100%;
        }

        .cb-button {
          width: 100%;
        }
      }

      &--confirm {
        .cb-registration__container__content__input {
          margin-right: 10px;
        }
      }
    }
  }

  @include desktop {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: unset;
    padding: 20px;

    &__img-container {
      display: block;
      overflow: hidden;
      height: 657px;
      width: 431px;

      img {
        height: 100%;
      }
    }

    .cb-navigation {
      &--mobile {
        display: none;
      }
      &--desktop {
        display: block;
      }
    }

    &__container {
      display: flex;
      height: 657px;
      max-width: 682px;
      margin: 0;
      box-sizing: border-box;
      padding: 20px 60px 28px;
      border-radius: 0;

      &__content {
        display: flex;
        flex-direction: column;

        form {
          display: flex;
          flex-grow: 1;
          flex-direction: column;
        }

        .cb-heading-4 {
          margin: 28px 0 12px;
        }

        .cb-body-1 {
          font-size: 18px;
          line-height: 27px;
        }

        &__name,
        &__password-container {
          display: flex;

          .space-right {
            margin-right: 10px;
          }

          .space-left {
            margin-left: 10px;
          }
        }

        &__password-container {
          .space-right,
          .space-left {
            flex-basis: 50%;
          }
        }

        &__password {
          .cb-tooltip {
            right: -206px;

            &__header {
              font-weight: 600;
              margin-bottom: 8px;
            }

            &::before {
              right: 169px;
            }

            &::after {
              right: 178px;
            }

            .assistance {
              margin: 12px 0 4px;
            }
          }
        }

        .cb-checkbox {
          margin: 0;
        }

        &__footer {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          justify-content: flex-end;
          margin: 0;
          flex-grow: 1;

          div:last-of-type {
            width: auto;
          }

          .divider {
            width: 100%;
            height: 1px;
            background-color: $light-blue-grey;
          }
          .cb-button {
            margin-top: 20px;
            width: auto;
          }
          .continue {
            align-self: flex-end;
          }
        }

        &--confirm {
          width: 100%;

          .cb-registration__container__content__input {
            margin: 0 20px 0 4px;
            flex-grow: 0;
          }

          .cb-registration__container__content__footer {
            div:last-of-type {
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
              width: 100%;

              label {
                margin-bottom: 10px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
